import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Card,
    Typography,
    IconButton,
    Stack,
    useTheme,
    Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const MarketTimings = () => {
    const navigate = useNavigate();
    const theme = useTheme();

    // Get current date and time in Indian timezone
    const now = new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' });
    const currentDate = new Date(now);
    const currentDay = currentDate.getDay(); // 0 is Sunday, 6 is Saturday
    const currentTime = currentDate.getHours() * 100 + currentDate.getMinutes(); // Convert to HHMM format

    // Market timing configurations
    const markets = [
        {
            name: 'NSE',
            openTime: '0916', // 9:16 AM
            closeTime: '1530', // 3:30 PM
            color: '#2E7D32', // Green
        },
        {
            name: 'MCX',
            openTime: '0901', // 9:01 AM
            closeTime: '2355', // 11:55 PM
            color: '#1976D2', // Blue
        },
    ];

    const isMarketOpen = (market) => {
        // Check if it's weekend
        if (currentDay === 0 || currentDay === 6) return false;

        const openTime = parseInt(market.openTime, 10);
        const closeTime = parseInt(market.closeTime, 10);
        return currentTime >= openTime && currentTime <= closeTime;
    };

    const formatDate = (date) => {
        const options = { day: '2-digit', month: 'long', year: 'numeric' };
        return date.toLocaleDateString('en-US', options);
    };

    const MarketCard = ({ market }) => {
        const isOpen = isMarketOpen(market);

        return (
            <Card
                sx={{
                    p: 3,
                    borderRadius: 2,
                    background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, rgba(45,45,60,0.95), rgba(45,45,60,0.9))'
                        : 'white',
                    boxShadow: theme.palette.mode === 'dark'
                        ? '0 8px 32px rgba(0,0,0,0.3)'
                        : '0 8px 32px rgba(145,158,171,0.16)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
                    },
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '4px',
                        height: '100%',
                        backgroundColor: market.color,
                    },
                }}
            >
                <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h5" fontWeight="bold" color={market.color}>
                            {market.name}
                        </Typography>
                        <Chip
                            label={isOpen ? 'OPEN' : 'CLOSED'}
                            color={isOpen ? 'success' : 'error'}
                            sx={{
                                fontWeight: 'bold',
                                minWidth: 80,
                                height: 28,
                            }}
                        />
                    </Stack>

                    <Stack spacing={1}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <AccessTimeIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                            <Typography variant="body1" color="text.secondary">
                                Opens at: {market.openTime.replace(/(\d{2})(\d{2})/, '$1:$2')} AM
                            </Typography>
                        </Stack>

                        <Stack direction="row" alignItems="center" spacing={1}>
                            <AccessTimeIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                            <Typography variant="body1" color="text.secondary">
                                Closes at: {market.closeTime.replace(/(\d{2})(\d{2})/, '$1:$2')} {parseInt(market.closeTime, 10) >= 1200 ? 'PM' : 'AM'}
                            </Typography>
                        </Stack>
                    </Stack>
                </Stack>
            </Card>
        );
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 800, mx: 'auto' }}>
            {/* Header */}
            <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ mb: 4 }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton
                        onClick={() => navigate(-1)}
                        sx={{
                            mr: 2,
                            width: 35,
                            height: 35,
                            bgcolor: theme.palette.mode === 'dark'
                                ? 'rgba(255,255,255,0.1)'
                                : 'rgba(0,0,0,0.05)',
                            backdropFilter: 'blur(6px)',
                            '&:hover': {
                                bgcolor: theme.palette.mode === 'dark'
                                    ? 'rgba(255,255,255,0.2)'
                                    : 'rgba(0,0,0,0.1)',
                                transform: 'translateX(-3px)',
                            },
                            transition: 'all 0.2s',
                            border: `1px solid ${theme.palette.mode === 'dark'
                                ? 'rgba(255,255,255,0.12)'
                                : 'rgba(0,0,0,0.08)'
                                }`,
                        }}
                    >
                        <ArrowBackIcon
                            sx={{
                                fontSize: 20,
                                color: theme.palette.mode === 'dark'
                                    ? 'rgba(255,255,255,0.8)'
                                    : 'rgba(0,0,0,0.7)'
                            }}
                        />
                    </IconButton>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 700,
                            background: theme.palette.mode === 'dark'
                                ? 'linear-gradient(45deg, #f3ec78 0%, #af4261 100%)'
                                : 'linear-gradient(45deg, #2c3e50 30%, #3498db 90%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                        }}
                    >
                        MARKET TIMINGS
                    </Typography>
                </Box>
            </Stack>

            {/* Today's Date */}
            <Card
                sx={{
                    p: 2,
                    mb: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, rgba(45,45,60,0.95), rgba(45,45,60,0.9))'
                        : 'white',
                }}
            >
                <CalendarTodayIcon
                    sx={{
                        color: theme.palette.primary.main,
                        fontSize: 24,
                    }}
                />
                <Typography variant="h6">
                    Today: {formatDate(currentDate)}
                </Typography>
            </Card>

            {/* Market Cards */}
            <Stack spacing={3}>
                {markets.map((market) => (
                    <MarketCard key={market.name} market={market} />
                ))}
            </Stack>
        </Box>
    );
};

export default MarketTimings;
